import React, { useEffect, useState } from "react";
import TrustdService from "../service/TrustdService";
import DateUtils from "../utils/DateUtils";
import AuthService from "../service/AmplifyAuth";
import ErrorUtils from "../utils/ErrorUtils";
import StringUtils from "../utils/StringUtils";
import EmailUtils from "../utils/EmailUtils";
import {
  Button,
  DateInput,
  Field,
  Modal,
  Panel,
  Spinner,
} from "../components/UI";
import {
  getCachedStripeUser,
  getStripeUserByEmail,
  useToast,
} from "../hooks";
import { useNavigate } from "../router";

const initialGuest = {
  email: "",
  phone: "",
  firstName: "",
  middleName: "",
  lastName: "",
  birthDate: "",
};
const initialBooking = {
  bookingId: "",
  listingId: "",
  listingName: "",
  listingChannel: "",
  checkInDate: "",
  checkOutDate: "",
};

export default function CheckScore() {
  const cachedUser = AuthService.getCachedUserAttributes?.() || null;
  const cachedDetailedUser = cachedUser?.username
    ? getCachedStripeUser(cachedUser.username)
    : null;
  const [guest, setGuest] = useState(initialGuest);
  const [booking, setBooking] = useState(initialBooking);
  const [detailedUser, setDetailedUser] = useState(cachedDetailedUser);
  const [listings, setListings] = useState([]);
  const [guestFormUrl, setGuestFormUrl] = useState("");
  const [guestFormId, setGuestFormId] = useState("");
  const [guestFormPageGuestEmail, setGuestFormPageGuestEmail] = useState("");
  const [sendEmailButtonDisabled, setSendEmailButtonDisabled] = useState(false);
  const [showShowEmailDialog, setShowShowEmailDialog] = useState(false);
  const [showGuestFormEndInformation, setShowGuestFormEndInformation] =
    useState(false);
  const [endFormText, setEndFormText] = useState("");
  const [loading, setLoading] = useState(false);
  const addToast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      const attrs = await AuthService.currentAuthenticatedUserAttributes();
      const cachedDetailed = getCachedStripeUser(attrs.username);
      if (cachedDetailed) {
        setDetailedUser(cachedDetailed);
      }
      const freshDetailed = await getStripeUserByEmail(attrs.username, {
        forceRefresh: true,
      });
      setDetailedUser(freshDetailed || cachedDetailed || null);
      const cachedListings = TrustdService.getCachedListings(attrs.username);
      if (cachedListings?.listings) setListings(cachedListings.listings.filter((item) => item.isActive));
      const listingResult = await TrustdService.getListings(attrs.username, { sync: false }).catch(() => ({ listings: cachedListings?.listings || [] }));
      setListings((listingResult.listings || []).filter((item) => item.isActive));
      void TrustdService.syncListings(attrs.username).then(() => TrustdService.getListings(attrs.username, { sync: false })).then((fresh) => setListings((fresh.listings || []).filter((item) => item.isActive))).catch(() => {});
    }
    load();
  }, []);

  const updateGuest = (field, value) =>
    setGuest((data) => ({ ...data, [field]: value }));
  const updateBooking = (field, value) =>
    setBooking((data) => ({ ...data, [field]: value }));

  const selectedListing = listings.find((listing) => listing.id === booking.listingId);
  const requiresBaseCredit = selectedListing
    ? selectedListing.isScreeningEnabled || selectedListing.isIdVerificationEnabled
    : true;
  const selfieRequired = selectedListing
    ? selectedListing.isSelfieVerificationEnabled
    : Boolean(detailedUser?.isSelfieVerificationEnabled);
  const requiredScreeningCredits = (requiresBaseCredit ? 1 : 0) + (selfieRequired ? 1 : 0);
  const checkUserHasScreening = () =>
    Number(detailedUser?.screening || 0) >= requiredScreeningCredits;

  const validateGuest = ({ requireBirthDate = false } = {}) => {
    let valid = true;
    if (StringUtils.isEmpty(guest.firstName)) {
      addToast({
        severity: "error",
        summary: "Validation Error",
        detail: "First Name is required",
        life: 5000,
      });
      valid = false;
    }
    if (!StringUtils.isEmpty(guest.email) && !EmailUtils.isValid(guest.email)) {
      addToast({
        severity: "error",
        summary: "Validation Error",
        detail: "Email is not a valid email address",
        life: 5000,
      });
      valid = false;
    }
    if (StringUtils.isEmpty(guest.lastName)) {
      addToast({
        severity: "error",
        summary: "Validation Error",
        detail: "Last Name is required",
        life: 5000,
      });
      valid = false;
    }
    if (StringUtils.isEmpty(booking.listingId)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Listing is required", life: 5000 });
      valid = false;
    }
    if (requireBirthDate && !DateUtils.isValid(guest.birthDate)) {
      addToast({
        severity: "error",
        summary: "Validation Error",
        detail: "Date of Birth is required for Trustd Score",
        life: 5000,
      });
      valid = false;
    }
    if (!DateUtils.isValid(booking.checkInDate)) {
      addToast({
        severity: "error",
        summary: "Validation Error",
        detail: "Check-In Date is required",
        life: 5000,
      });
      valid = false;
    }
    if (!DateUtils.isValid(booking.checkOutDate)) {
      addToast({
        severity: "error",
        summary: "Validation Error",
        detail: "Check-Out Date is required",
        life: 5000,
      });
      valid = false;
    }
    return valid;
  };

  const prepareGuestFormUrl = async () => {
    if (loading) return;
    if (!validateGuest()) return;
    if (!checkUserHasScreening()) {
      addToast({
        severity: "error",
        summary: "Guest Form Send Error",
        detail:
          requiredScreeningCredits === 2
            ? "Selfie Verification requires 2 screening credits in total. Top up your account to continue."
            : "Your Screening Credit balance is zero. Top up your account to continue.",
        life: 5000,
      });
      return;
    }
    setLoading(true);
    const normalizedEmail = String(guest.email || "").trim();
    setGuestFormPageGuestEmail(normalizedEmail);
    setSendEmailButtonDisabled(!EmailUtils.isValid(normalizedEmail));
    try {
      const id = await TrustdService.saveGuestFormRequest(
        guest,
        booking,
        "pending",
        undefined,
        {
          idVerificationRequired: Boolean(
            selectedListing?.isIdVerificationEnabled ?? detailedUser?.isIdVerificationEnabled,
          ),
          selfieVerificationRequired: selfieRequired,
        },
      );
      setGuestFormId(id);
      setGuestFormUrl(`${window.location.origin}/guestForm/${id}`);
      setShowShowEmailDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const saveGuestEmailIfChanged = async () => {
    const normalizedGuestEmail = String(guest.email || "").trim();
    const isValid = EmailUtils.isValid(normalizedGuestEmail);
    setSendEmailButtonDisabled(!isValid);
    if (!isValid) return false;
    if (guestFormPageGuestEmail !== normalizedGuestEmail) {
      setLoading(true);
      try {
        await TrustdService.updateGuestFormRequestGuest(guestFormId, {
          ...guest,
          email: normalizedGuestEmail,
        });
        setGuestFormPageGuestEmail(normalizedGuestEmail);
        return true;
      } catch (error) {
        addToast({
          severity: "error",
          summary: "Guest Email Save Error",
          detail: "Guest email could not be saved!",
          life: 5000,
        });
        return false;
      } finally {
        setLoading(false);
      }
    }
    return true;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    addToast({
      severity: "success",
      summary: "URL copied",
      detail: "Guest Form URL copied to clipboard",
      life: 5000,
    });
  };

  const handleGuestFormEndInformation = (status) => {
    if (status !== "SENT") {
      copyToClipboard(guestFormUrl);
    }
    setEndFormText(
      status === "SENT"
        ? "Email is sent to your guest successfully"
        : "Do not forget to send the URL to your guest later. You can text it to your guest, or you can use messaging function of your booking channel, or PMS, if you use one.",
    );
    setShowShowEmailDialog(false);
    setShowGuestFormEndInformation(true);
  };

  const sendGuestFormEmail = async () => {
    if (loading) return;
    if (sendEmailButtonDisabled) return;
    if (!EmailUtils.isValid(guest.email)) {
      addToast({
        severity: "error",
        summary: "Validation Error",
        detail: "Email is not a valid email address",
        life: 5000,
      });
      return;
    }
    let isSent = false;
    setLoading(true);
    try {
      const saved = await saveGuestEmailIfChanged();
      if (!saved) {
        setSendEmailButtonDisabled(false);
        return;
      }

      setSendEmailButtonDisabled(true);
      const resp = await TrustdService.sendGuestFormEmail(
        guestFormId,
        guestFormUrl,
      );
      if (resp?.errorData) {
        addToast({
          severity: "error",
          summary: "Guest Form Send Error",
          detail:
            ErrorUtils.parseError(resp.errorData) ||
            "Guest Form could not be sent via email",
          life: 7000,
        });
        setSendEmailButtonDisabled(false);
        return;
      }
      addToast({
        severity: "success",
        summary: "Guest Form sent successfully",
        detail: "Guest Form sent successfully via email",
        life: 5000,
      });
      isSent = true;
    } catch (error) {
      addToast({
        severity: "error",
        summary: "GuestForm Send Error",
        detail: ErrorUtils.parseError(error),
        life: 5000,
      });
    } finally {
      setLoading(false);
      if (isSent) {
        handleGuestFormEndInformation("SENT");
      }
    }
  };

  const screeningCredits = Number(detailedUser?.screening || 0);
  const guestDisplayName =
    [guest.firstName, guest.lastName].filter(Boolean).join(" ") ||
    "[Guest Name]";
  const propertyDisplayName = String(booking.listingName || "").trim();
  const propertySubjectPhrase = propertyDisplayName
    ? ` at ${propertyDisplayName}`
    : "";
  const propertyBodyPhrase = propertyDisplayName
    ? ` at ${propertyDisplayName}`
    : "";
  const checkInDisplayDate =
    DateUtils.convertToMMYYDDDD?.(booking.checkInDate) || "[Check-in Date]";
  const checkOutDisplayDate =
    DateUtils.convertToMMYYDDDD?.(booking.checkOutDate) || "[Check-out Date]";
  const hostDisplayName =
    detailedUser?.contactName || detailedUser?.company || "[Host Name]";
  const guestFormPreviewUrl =
    guestFormUrl || "[Link: https://portal.trustd.ai/guestForm/...]";
  const guestFormEmailSubject = `Action Required: Guest verification for your upcoming stay${propertySubjectPhrase}!`;

  if (!detailedUser) return <div className="loading-container" />;

  return (
    <>
      {loading ? <Spinner /> : null}
      <div className="checkScore checkscore-modern">
        <div className="checkscore-form checkscore-shell">
          <div className="checkscore-page-head">
            <div>
              <p className="dashboard-section-kicker">Guest Screening</p>
              <h1>Share a secure guest form</h1>
              <p className="checkscore-subtitle">
                Enter guest and reservation details, then send a Trustd
                verification form for the guest to complete.
              </p>
            </div>
            <div className="checkscore-credit-pill">
              <span>Credits Available</span>
              <strong>{screeningCredits}</strong>
            </div>
          </div>

          <Panel header="Guest Information" className="checkscore-panel">
            <div className="checkscore-fields-grid">
              <Field label="Email">
                <input
                  type="email"
                  className="form-control p-1"
                  value={guest.email}
                  placeholder="Guest Email Address"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck="false"
                  onChange={(event) => updateGuest("email", event.target.value)}
                />
              </Field>
              <Field label="Phone">
                <input
                  className="form-control p-1"
                  value={guest.phone}
                  placeholder="Guest Phone Number"
                  autoComplete="tel-national"
                  inputMode="tel"
                  onChange={(event) => updateGuest("phone", event.target.value)}
                />
              </Field>
              <Field label="First Name" required>
                <input
                  className="form-control p-1"
                  value={guest.firstName}
                  placeholder="Guest First Name"
                  autoComplete="given-name"
                  onChange={(event) =>
                    updateGuest("firstName", event.target.value)
                  }
                />
              </Field>
              <Field label="Middle Name">
                <input
                  className="form-control p-1"
                  value={guest.middleName}
                  placeholder="Guest Middle Name"
                  onChange={(event) =>
                    updateGuest("middleName", event.target.value)
                  }
                />
              </Field>
              <Field label="Last Name" required>
                <input
                  className="form-control p-1"
                  value={guest.lastName}
                  placeholder="Guest Last Name"
                  autoComplete="family-name"
                  onChange={(event) =>
                    updateGuest("lastName", event.target.value)
                  }
                />
              </Field>
              <Field label="Date of Birth">
                <DateInput
                  value={guest.birthDate}
                  onChange={(value) => updateGuest("birthDate", value)}
                />
              </Field>
            </div>
          </Panel>

          <Panel header="Reservation Information" className="checkscore-panel">
            <div className="checkscore-fields-grid">
              <Field label="Booking Id">
                <input
                  className="form-control p-1"
                  value={booking.bookingId}
                  placeholder="Booking ID"
                  onChange={(event) =>
                    updateBooking("bookingId", event.target.value)
                  }
                />
              </Field>
              <Field label="Listing">
                <select
                  className="form-control p-1"
                  value={booking.listingId}
                  onChange={(event) => {
                    const listing = listings.find((item) => item.id === event.target.value);
                    setBooking((current) => ({ ...current, listingId: listing?.id || "", listingName: listing?.name || "", listingChannel: listing?.provider || current.listingChannel }));
                  }}
                >
                  <option value="">Select a listing</option>
                  {listings.map((listing) => <option key={listing.id} value={listing.id}>{listing.name}</option>)}
                </select>
              </Field>
              <Field label="Listing Channel">
                <input
                  className="form-control p-1"
                  value={booking.listingChannel}
                  placeholder="Listing Channel"
                  onChange={(event) =>
                    updateBooking("listingChannel", event.target.value)
                  }
                />
              </Field>
              <div></div>
              <Field label="Check-In Date" required>
                <DateInput
                  value={booking.checkInDate}
                  onChange={(value) => updateBooking("checkInDate", value)}
                />
              </Field>
              <Field label="Check-Out Date" required>
                <DateInput
                  value={booking.checkOutDate}
                  onChange={(value) => updateBooking("checkOutDate", value)}
                />
              </Field>
            </div>
          </Panel>

          <div className="checkscore-action-text checkscore-actions">
            <Button
              className="checkscore-btn-primary checkscore-share-cta"
              disabled={loading}
              onClick={prepareGuestFormUrl}
            >
              <i className="pi pi-send" aria-hidden="true" />
              <span>{loading ? "Preparing..." : "Share Form With Guest"}</span>
            </Button>
          </div>
        </div>
      </div>

      <Modal
        visible={showShowEmailDialog}
        title="Guest Form Email"
        description="Preview the message your guest will receive."
        icon="pi-send"
        onClose={() => setShowShowEmailDialog(false)}
        width="min(90vw, 760px)"
      >
        <div className="checkscore-modal-content">
          <div className="guest-email-preview" aria-label="Guest email preview">
            <div className="guest-email-preview-subject">
              <span>Subject</span>
              <strong>{guestFormEmailSubject}</strong>
            </div>
            <div className="guest-email-preview-body">
              <p>Hello {guestDisplayName},</p>
              <p>
                Thank you for your recent booking with us
                {propertyBodyPhrase} from {checkInDisplayDate} to{" "}
                {checkOutDisplayDate}. To finalize your reservation, please
                complete the required guest screening and ID verification steps
                through our partner, Trustd, by clicking the link below:
              </p>
              <div className="guest-email-preview-link">
                <span>Secure Verification Link:</span>
                <strong>{guestFormPreviewUrl}</strong>
              </div>
              <p>
                Once this step is completed, we'll be all set to send over your
                check-in details.
              </p>
              <p>We look forward to hosting you!</p>
              <p className="guest-email-preview-signature">
                Best Regards
                <br />
                {hostDisplayName}
              </p>
            </div>
          </div>
          <Field label="Guest Email Address" className="mt-4">
            <input
              className="form-control"
              value={guest.email}
              autoComplete="email"
              autoCapitalize="none"
              spellCheck="false"
              onChange={(event) => {
                const nextEmail = event.target.value;
                updateGuest("email", nextEmail);
                setSendEmailButtonDisabled(!EmailUtils.isValid(nextEmail));
              }}
              onBlur={saveGuestEmailIfChanged}
            />
          </Field>
          <div className="input-with-button mt-3">
            <input className="form-control" value={guestFormUrl} readOnly />
            <div className="modal-actions-vertical">
              <Button
                label="Copy and Send Later"
                className="btn-secondary"
                disabled={loading}
                onClick={() => handleGuestFormEndInformation("LATER")}
              />
              <Button
                label={loading ? "Sending..." : "Send Now"}
                disabled={loading || sendEmailButtonDisabled}
                onClick={sendGuestFormEmail}
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        visible={showGuestFormEndInformation}
        title="Guest Form"
        icon="pi-check-circle"
        tone="success"
        width="min(90vw, 650px)"
      >
        <p className="text-lg">{endFormText}</p>
        <div className="modal-actions">
          <Button
            label="OK"
            onClick={() => {
              setGuest(initialGuest);
              setBooking(initialBooking);
              navigate("/");
            }}
          />
        </div>
      </Modal>
    </>
  );
}
